import { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { settingsForTarget, visibleSettings } from "../settings/catalog";
import { useSettings } from "../settings/context";
import { Appearance } from "./Apparence";
import { Row } from "./Row";
import { shownIn, useSituation } from "./situation";

/**
 * SETTINGS WHERE YOU ARE LOOKING.
 *
 * Click a block on the page and its settings open beside it. The right-hand
 * panel keeps the full list; this is the shortcut for the thing you can already
 * see and want to change now — no hunting through a panel that will hold some
 * two hundred and fifty entries.
 *
 * IT OPENS ON HOVER. The delays and the guards that make that bearable live in
 * `Shell.tsx`; what belongs here is the third of them — THE CARD IS A HOVER
 * TARGET IN ITS OWN RIGHT. Entering it stops whatever was closing it, which is
 * what turns the gap between block and card into something crossable rather than
 * fatal.
 *
 * It is closed by leaving it, by Escape, by clicking outside, and by leaving
 * organiser mode.
 *
 * Displayed strings are French: the organiser is a French speaker.
 */

/** Distance kept from the block and from the window edges. */
const MARGIN = 10;
const WIDTH = 300;

type Placement = { left: number; top: number };

/**
 * Where the card goes: to the RIGHT of the block, failing that to its LEFT,
 * failing that against the work area's RIGHT EDGE.
 *
 * ⚠️ It is confined to the WORK AREA — the space the site occupies, never the
 * settings panel. Left to the whole window, a block reaching the right-hand edge
 * of the site pushed its card straight over the panel: a tool hiding its own
 * controls.
 *
 * The third placement is for blocks that span the width, where nothing fits
 * beside them. It overlaps their right-hand end, and that is the deliberate
 * choice: text starts on the left, so the block stays readable, the card always
 * turns up in the SAME place, and — this is what settles it — nothing BELOW is
 * covered. Placing it under the block hid the very button sitting there; you had
 * to close the card before you could click anything again.
 */
function place(anchor: DOMRect, height: number, region: DOMRect): Placement {
  const min = region.left + MARGIN;
  const max = region.right - MARGIN - WIDTH;

  let left = anchor.right + MARGIN;
  if (left > max) left = anchor.left - WIDTH - MARGIN;
  if (left < min) left = Math.max(min, max);

  let top = anchor.top;
  if (top + height > window.innerHeight - MARGIN) {
    top = window.innerHeight - height - MARGIN;
  }
  return { left, top: Math.max(MARGIN, top) };
}

export function SpotEditor({
  element,
  page,
  instance,
  onKeep,
  onRelease,
  onClose,
}: {
  element: HTMLElement;
  page: string;
  /** L'instance d'étape répétable regardée, pour suffixer ses clés — voir `settingsForTarget`. */
  instance?: string;
  /** The pointer has entered the card: whatever was closing it must stop. */
  onKeep: () => void;
  /** The pointer has left: closing may resume, after its grace period. */
  onRelease: () => void;
  onClose: () => void;
}) {
  const card = useRef<HTMLDivElement>(null);
  const [at, setAt] = useState<Placement | null>(null);
  const { value, typos, forms } = useSettings();
  const situation = useSituation();

  const target = element.dataset["cfg"] ?? "";
  // MEMOISED, and it matters: the positioning effect depends on it, and a fresh
  // object on every render restarted that effect endlessly — `setAt` re-rendered,
  // which rebuilt the object, which restarted the effect. React tore the whole
  // tree down rather than loop.
  // The typologies travel with the lookup: without them the card that opens beside
  // a generated block would come back empty, and the organiser would read that as
  // "nothing configures this".
  const found = useMemo(
    () =>
      settingsForTarget(page, target, {
        typos: typos.flatMap((t) =>
          t.id === null ? [] : [{ id: t.id, name: t.name }],
        ),
        forms,
        // ⚠️ L'INSTANCE VOYAGE JUSQU'ICI. Sans elle, la carte au survol d'un bloc de la 2e
        // étape « informations » écrivait dans les clés de la PREMIÈRE (non suffixées) et
        // reconfigurait l'étape 1 en silence. Voir `instanceKey` dans `catalog.ts`.
        ...(instance ? { instance } : {}),
      }),
    [page, target, typos, forms, instance],
  );

  // Measured after paint, then positioned: the height depends on how many
  // settings the block carries, so it cannot be guessed.
  useLayoutEffect(() => {
    const box = card.current;
    if (!box) return;
    const reposition = () => {
      // The work area, not the window: the card must never land on the panel.
      const region =
        document.querySelector(".stagearea")?.getBoundingClientRect() ??
        new DOMRect(0, 0, window.innerWidth, window.innerHeight);
      setAt(place(element.getBoundingClientRect(), box.offsetHeight, region));
    };
    reposition();

    // The page scrolls under the card, and the window can be resized. `true`
    // catches scrolling inside `.scroll`, which does not reach the window.
    window.addEventListener("scroll", reposition, true);
    window.addEventListener("resize", reposition);
    return () => {
      window.removeEventListener("scroll", reposition, true);
      window.removeEventListener("resize", reposition);
    };
  }, [element, found]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => e.key === "Escape" && onClose();
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose]);

  if (!found) return null;

  return (
    <div
      ref={card}
      className="spot"
      role="dialog"
      aria-label={`Réglages : ${found.title}`}
      onMouseEnter={onKeep}
      onMouseLeave={onRelease}
      // Hidden until measured: showing it at 0,0 for one frame reads as a jump.
      style={{
        left: at?.left ?? 0,
        top: at?.top ?? 0,
        visibility: at ? "visible" : "hidden",
      }}
    >
      <div className="spot__h">
        <b>{found.title}</b>
        <button
          type="button"
          className="spot__x"
          onClick={onClose}
          aria-label="Fermer"
        >
          ✕
        </button>
      </div>
      <div className="spot__b">
        {/* Filtered exactly as the panel filters — same function, so a setting
            cannot be present here and gone there. */}
        {visibleSettings(
          found.settings.filter(
            (s) => !s.managed && shownIn(s, situation),
          ),
          value,
        ).map((setting) => (
          <Row key={setting.key} setting={setting} />
        ))}

        {/* ⚠️ LA PRISE EST ICI, ET NON DANS UN SECOND MODE. La carte sait déjà quel
            bloc on désigne — son nom, et son élément. Un « mode dessin » à curseur en
            croix aurait été un DEUXIÈME geste pour la même intention, et il aurait
            fallu savoir lequel donne quoi. */}
        <Appearance title={found.title} target={target} element={element} />
      </div>
    </div>
  );
}
